import { Box, Flex, Stack, Text, Tooltip } from '@mantine/core';
import clsx from 'clsx';
import classes from './WorkTypeAnalysisChart.module.css';
import {
  WORK_TYPE_MESSAGE_IDS,
  WorktypeAnalysisRating,
} from '@/features/types';
import type { WorkTypeRow, WorkTypeView } from '@/features/workTypeAnalysis';
import { useClientTerminology } from '@/views/list/NetworkTerms';

interface WorkTypeAnalysisChartProps {
  rows: WorkTypeRow[];
  view: WorkTypeView;
  onViewChange: (view: WorkTypeView) => void;
  formatAmount: (row: WorkTypeRow) => string;
  'data-testid'?: string;
}

const COLOR_CLASS: Record<WorktypeAnalysisRating, string> = {
  [WorktypeAnalysisRating.Herbicide_Zone]: classes.herbicide,
  [WorktypeAnalysisRating.Mow_Zone]: classes.mow,
  [WorktypeAnalysisRating.Cut_Zone]: classes.cut,
  [WorktypeAnalysisRating.Side_Wall]: classes.sideWall,
  [WorktypeAnalysisRating.Not_Vegetation]: classes.noWorkType,
  [WorktypeAnalysisRating.Error]: classes.noWorkType,
};

/**
 * The work types of one span, with a horizontal stacked bar in the area view
 */
export const WorkTypeAnalysisChart = ({
  rows,
  view,
  onViewChange,
  formatAmount,
  'data-testid': testId = 'work-type-chart',
}: WorkTypeAnalysisChartProps) => {
  const { formatMessage } = useClientTerminology();
  const segments = rows.filter((row) => row.percent > 0);

  return (
    <Stack gap={0} data-testid={testId}>
      <Flex gap="xxs" pt="xs" pb="xs">
        {(['area', 'linear'] as const).map((option, index) => (
          <Flex key={option} gap="xxs">
            {index > 0 && (
              <Text size="sm" c="dimmed">
                |
              </Text>
            )}
            <Text
              size="sm"
              fw={view === option ? 700 : 400}
              c={view === option ? undefined : 'dimmed'}
              className={classes.title}
              onClick={() => onViewChange(option)}
              data-testid={`${testId}-view-${option}`}
            >
              {formatMessage({
                id:
                  option === 'area'
                    ? 'level3.workType.byArea'
                    : 'level3.workType.linearExtent',
              })}
            </Text>
          </Flex>
        ))}
      </Flex>
      {view === 'area' && (
        <Box pb="md">
          <Flex
            gap={0}
            className={classes.barRow}
            data-testid={`${testId}-bar`}
          >
            {segments.map((row, index) => (
              <Tooltip
                key={row.rating}
                label={`${formatMessage({ id: WORK_TYPE_MESSAGE_IDS[row.rating] })}: ${formatAmount(row)} (${Math.round(row.percent)}%)`}
                withArrow
                position="bottom"
              >
                <Box
                  className={clsx(classes.segment, COLOR_CLASS[row.rating], {
                    [classes.segmentFirst]: index === 0,
                    [classes.segmentLast]: index === segments.length - 1,
                  })}
                  w={`${row.percent}%`}
                  data-testid={`${testId}-segment-${WorktypeAnalysisRating[row.rating]}`}
                />
              </Tooltip>
            ))}
          </Flex>
        </Box>
      )}
      {rows.map((row) => (
        <Flex
          key={row.rating}
          justify="space-between"
          align="center"
          gap="xs"
          py="xs"
          data-testid={`${testId}-row-${WorktypeAnalysisRating[row.rating]}`}
        >
          <Flex gap="xs" align="center">
            <Box className={clsx(classes.swatch, COLOR_CLASS[row.rating])} />
            <Text size="sm">
              {formatMessage({ id: WORK_TYPE_MESSAGE_IDS[row.rating] })}
            </Text>
          </Flex>
          <Flex gap="sm" align="center">
            <Text size="sm" c="dimmed">
              {formatAmount(row)}
            </Text>
            <Text size="sm" fw="medium" w={40} ta="right">
              {Math.round(row.percent)}%
            </Text>
          </Flex>
        </Flex>
      ))}
    </Stack>
  );
};
